<script generics="T extends string" lang="ts">
	import ChevronDown from '@lucide/svelte/icons/chevron-down'
	import ChevronsUpDown from '@lucide/svelte/icons/chevrons-up-down'
	import { DropdownMenu as DropdownMenuPrimitive, Accordion } from 'bits-ui'

	import Badge from '#lib/components/ui/badge/badge.svelte'
	import { Button } from '#lib/components/ui/button/index.js'
	import * as DropdownMenu from '#lib/components/ui/dropdown-menu/index.js'
	import { n } from '#lib/utils/navigation.svelte.js'

	type Item = {
		label: string
		value: T
		isNew?: boolean
	}

	type Option = {
		label: string
		categoryName: string
		items: Item[]
	}

	let {
		align = 'end',
		metrics,
		metric = $bindable(),
		onValueChange,
	}: {
		align?: DropdownMenuPrimitive.ContentProps['align']
		metrics: Option[] | Item[]
		metric: T
		onValueChange: (value: string) => void
	} = $props()

	const isGrouped = (metrics: Option[] | Item[]): metrics is Option[] =>
		metrics.length > 0 && 'categoryName' in metrics[0]

	let accordionValue = $derived(
		isGrouped(metrics)
			? (metrics.find(cat => cat.items.some(item => item.value === metric))?.categoryName ?? '')
			: ''
	)
</script>

{#snippet item({ value, label, isNew }: { value: T; label: string; isNew?: boolean })}
	<DropdownMenu.RadioItem
		class="text-xs whitespace-nowrap [&_svg:not([class*='size-'])]:size-3"
		{value}
	>
		{label}

		{#if isNew}
			<Badge class="ml-1 uppercase" size="4xs" variant="brand">New</Badge>
		{/if}
	</DropdownMenu.RadioItem>
{/snippet}

<DropdownMenu.Root>
	<DropdownMenu.Trigger>
		{#snippet child({ props })}
			<Button disabled={n.l} variant="outline" {...props} size="sm">
				{(isGrouped(metrics)
					? metrics
							.map(({ items }) => items)
							.flat()
							.find(({ value }) => value === metric)?.label
					: metrics.find(({ value }) => value === metric)?.label) ?? 'Select metric'}

				<ChevronsUpDown />
			</Button>
		{/snippet}
	</DropdownMenu.Trigger>

	<DropdownMenu.Content class="w-52" {align} preventScroll={false}>
		<DropdownMenu.RadioGroup {onValueChange} bind:value={metric}>
			{#if isGrouped(metrics)}
				<Accordion.Root type="single" bind:value={accordionValue}>
					{#each metrics as { categoryName, label, items }, i (label)}
						{#if i !== 0}
							<DropdownMenu.Separator />
						{/if}

						<Accordion.Item value={categoryName}>
							<Accordion.Header>
								<Accordion.Trigger
									class="hover:bg-accent hover:text-accent-foreground flex w-full flex-1 items-center justify-between rounded-sm select-none [&[data-state=open]>span>svg]:rotate-180"
								>
									<DropdownMenu.Label>
										{label}
									</DropdownMenu.Label>

									<span
										class="text-muted-foreground flex size-8 items-center justify-center rounded-lg"
									>
										<ChevronDown class="size-3 transition-transform duration-200" size="14" />
									</span>
								</Accordion.Trigger>
							</Accordion.Header>

							<Accordion.Content>
								<DropdownMenu.Group>
									{#each items as { value, label, isNew = false } (value)}
										{@render item({ isNew, label, value })}
									{/each}
								</DropdownMenu.Group>
							</Accordion.Content>
						</Accordion.Item>
					{/each}
				</Accordion.Root>
			{:else}
				{#each metrics as { value, label, isNew = false } (value)}
					{@render item({ isNew, label, value })}
				{/each}
			{/if}
		</DropdownMenu.RadioGroup>
	</DropdownMenu.Content>
</DropdownMenu.Root>
